import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Chip, IconButton, Tooltip, Paper, TextField } from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CodeIcon from "@mui/icons-material/Code";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { setModal } from "../../features/modal/ModalSlice";
import CustomButton from "../../components/Button/CustomButton";

const Widget = () => {
  const dispatch = useDispatch();
  const modal = useSelector((state) => state.modal?.modal);

  const [widgets, setWidgets] = useState([
    {
      id: 1,
      name: "Customer Support Chat",
      type: "Chat",
      assistant: "Customer Support Agent",
      position: "Bottom Right",
      theme: "Light",
      status: "Active",
      installCount: 3,
      interactions: 1245,
      createdAt: "2024-01-10",
    },
    {
      id: 2,
      name: "Voice Assistant Button",
      type: "Voice",
      assistant: "Sales Agent",
      position: "Bottom Left",
      theme: "Dark",
      status: "Active",
      installCount: 2,
      interactions: 567,
      createdAt: "2024-01-08",
    },
    {
      id: 3,
      name: "Product Help Widget",
      type: "Hybrid",
      assistant: "Technical Support",
      position: "Bottom Right",
      theme: "Custom",
      status: "Active",
      installCount: 5,
      interactions: 2134,
      createdAt: "2024-01-05",
    },
    {
      id: 4,
      name: "Appointment Booking",
      type: "Chat",
      assistant: "Appointment Scheduler",
      position: "Center",
      theme: "Light",
      status: "Draft",
      installCount: 0,
      interactions: 0,
      createdAt: "2024-01-15",
    },
  ]);

  const [selectedWidget, setSelectedWidget] = useState(widgets[0]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (modal?.isUpdatedIn === "WIDGET") {
      fetchWidgets();
    }
  }, [modal]);

  const fetchWidgets = async () => {
    setLoading(true);
    try {
      setTimeout(() => setLoading(false), 500);
    } catch (error) {
      console.error("Error fetching widgets:", error);
      setLoading(false);
    }
  };

  const handleCreateWidget = () => {
    dispatch(setModal({ type: "CREATE_WIDGET" }));
  };

  const handleEditWidget = (widget) => {
    dispatch(setModal({ type: "EDIT_WIDGET", data: widget }));
  };

  const handleDeleteWidget = (widget) => {
    if (window.confirm(`Delete widget "${widget.name}"?`)) {
      setWidgets(widgets.filter((w) => w.id !== widget.id));
    }
  };

  const handlePreviewWidget = (widget) => {
    dispatch(setModal({ type: "PREVIEW_WIDGET", data: widget }));
  };

  const handleCopyEmbedCode = (widget) => {
    const embedCode = `<script src="https://cdn.vapi.com/widget.js" data-widget-id="${widget.id}"></script>`;
    navigator.clipboard.writeText(embedCode);
    alert("Embed code copied to clipboard!");
  };

  const getTypeColor = (type) => {
    const colors = {
      Chat: { bg: "#E3F2FD", color: "#1565C0" },
      Voice: { bg: "#E8F5E9", color: "#2E7D32" },
      Hybrid: { bg: "#F3E5F5", color: "#7B1FA2" },
    };
    return colors[type] || { bg: "#F5F5F5", color: "#666" };
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Widget Name",
        size: 200,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}
            >
              <CodeIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {row.original.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.original.assistant}
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        accessorKey: "type",
        header: "Type",
        size: 100,
        Cell: ({ cell }) => {
          const colors = getTypeColor(cell.getValue());
          return (
            <Chip
              label={cell.getValue()}
              size="small"
              sx={{
                backgroundColor: colors.bg,
                color: colors.color,
                fontWeight: 600,
              }}
            />
          );
        },
      },
      {
        accessorKey: "position",
        header: "Position",
        size: 120,
      },
      {
        accessorKey: "theme",
        header: "Theme",
        size: 100,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 100,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue()}
            size="small"
            color={cell.getValue() === "Active" ? "success" : "default"}
            sx={{ fontWeight: 500 }}
          />
        ),
      },
      {
        accessorKey: "installCount",
        header: "Installs",
        size: 80,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={600} color="primary">
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "interactions",
        header: "Interactions",
        size: 100,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={600}>
            {cell.getValue().toLocaleString()}
          </Typography>
        ),
      },
    ],
    []
  );

  const totalWidgets = widgets.length;
  const activeWidgets = widgets.filter((w) => w.status === "Active").length;
  const totalInstalls = widgets.reduce((sum, w) => sum + w.installCount, 0);
  const totalInteractions = widgets.reduce((sum, w) => sum + w.interactions, 0);

  const embedCode = `<script src="https://cdn.vapi.com/widget.js" data-widget-id="${selectedWidget.id}"></script>`;

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            Voice & Chat Widgets
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create embeddable widgets for your website
          </Typography>
        </Box>
        <CustomButton
          icon={<AddIcon />}
          iconPosition="left"
          onClick={handleCreateWidget}
          text="Create Widget"
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <Box sx={{ flex: 1, p: 2, borderRadius: 2, backgroundColor: "#F3F4F6" }}>
          <Typography variant="h4" fontWeight={700} color="primary">
            {totalWidgets}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total Widgets
          </Typography>
        </Box>
        <Box sx={{ flex: 1, p: 2, borderRadius: 2, backgroundColor: "#F3F4F6" }}>
          <Typography variant="h4" fontWeight={700} color="success.main">
            {activeWidgets}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Active Widgets
          </Typography>
        </Box>
        <Box sx={{ flex: 1, p: 2, borderRadius: 2, backgroundColor: "#F3F4F6" }}>
          <Typography variant="h4" fontWeight={700} color="info.main">
            {totalInstalls}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total Installs
          </Typography>
        </Box>
        <Box sx={{ flex: 1, p: 2, borderRadius: 2, backgroundColor: "#F3F4F6" }}>
          <Typography variant="h4" fontWeight={700} color="warning.main">
            {totalInteractions.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total Interactions
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: "1px solid #E0E0E0" }}>
        <Typography variant="h6" gutterBottom fontWeight={600}>
          Embed Code
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Copy and paste this code into your website's HTML
        </Typography>
        <Box sx={{ mt: 2, position: "relative" }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={embedCode}
            InputProps={{
              readOnly: true,
              sx: {
                fontFamily: "monospace",
                fontSize: 12,
                backgroundColor: "#F9FAFB",
              },
            }}
          />
          <IconButton
            size="small"
            onClick={() => handleCopyEmbedCode(selectedWidget)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              backgroundColor: "#FFF",
              "&:hover": { backgroundColor: "#F5F5F5" },
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      <MaterialReactTable
        columns={columns}
        data={widgets}
        state={{ isLoading: loading }}
        enableRowActions
        positionActionsColumn="last"
        renderRowActions={({ row }) => (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Preview">
              <IconButton
                size="small"
                onClick={() => handlePreviewWidget(row.original)}
                sx={{ color: "info.main" }}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy Embed Code">
              <IconButton
                size="small"
                onClick={() => handleCopyEmbedCode(row.original)}
                sx={{ color: "success.main" }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => handleEditWidget(row.original)}
                sx={{ color: "primary.main" }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => handleDeleteWidget(row.original)}
                sx={{ color: "error.main" }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        enableColumnFilters
        enableGlobalFilter
        enableSorting
        enablePagination={false}
        muiTablePaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 2,
            border: "1px solid #E0E0E0",
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            backgroundColor: "#F9FAFB",
            fontWeight: 600,
          },
        }}
      />
    </Box>
  );
};

export default Widget;
